import useFileUpload from 'Components/Chat/hooks/attachments/useFileUpload';
import { useAttachmentStore } from 'Components/Chat/store/attachments';
import {
  AttachmentItem,
  S3Object,
} from 'Components/Chat/store/attachments/index.types';
import { useMaxFileSizeBytes } from 'Hooks/config/useMessagingConfig';
import { FileTypeProps, fileToBase64, mapIcon } from 'Models/FileType';
import { useNotificationStore } from 'Modules/notification/store';
import {
  ANALYTICS_EVENT_FILE_UPLOAD_ATTEMPTED,
  ANALYTICS_EVENT_FILE_UPLOAD_ERROR_DISPLAYED,
  ANALYTICS_EVENT_FILE_UPLOAD_FAILED_SIZE_LIMIT,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import { formatFileSize, isFileLargerThanBytes } from 'Utils/fileUtils';
import { resizeImage } from 'Utils/resizeImage';
import { v4 as uuidv4 } from 'uuid';

export const useAttachments = () => {
  const { addAttachment, resetAttachments } = useAttachmentStore();
  const { uploadAll } = useFileUpload();
  const maxFileSizeBytes = useMaxFileSizeBytes();
  const getAttachmentsCount = useAttachmentStore(
    (state) => state.attachments.length
  );

  const hasUploadErrors = useAttachmentStore((state) =>
    state.attachments.some((a) => a.status === 'error')
  );
  const hasUploadsInProgress = useAttachmentStore((state) =>
    state.attachments.some((a) => a.status === 'uploading')
  );
  const uploadedFilesMap = useAttachmentStore((state) =>
    state.attachments.map<FileTypeProps>((a) => a.file)
  );
  const uploadedS3ObjectsMap = useAttachmentStore((state) => {
    return state.attachments.map<S3Object>((a) => a.s3Object);
  });
  // FIXME: For some reason, we can't use the above selector in the src\app\containers\ContextPanel\index.tsx ;
  // we need this function so it gets evaluated with the right state
  const getUploadedS3ObjectsMap = () => {
    return useAttachmentStore
      .getState()
      .attachments.map<S3Object>((a) => a.s3Object);
  };

  const addAttachmentsViaEvent = async (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ClipboardEvent<HTMLInputElement>
  ) => {
    const selectedFiles =
      'clipboardData' in e
        ? e.clipboardData?.files
        : e.target?.files || ([] as unknown as FileList);

    if (selectedFiles) {
      await addAttachments(selectedFiles);
    }
  };

  const addAttachments = async (selectedFiles: FileList) => {
    const attachments: AttachmentItem[] = [];
    const acceptedFiles: File[] = [];
    let oversizedFileCount = 0;

    for (const file of selectedFiles) {
      pushToGTMDataLayer(ANALYTICS_EVENT_FILE_UPLOAD_ATTEMPTED, {
        file_size: file.size,
        file_type: file.type,
      });

      if (
        maxFileSizeBytes !== null &&
        isFileLargerThanBytes(file, maxFileSizeBytes)
      ) {
        oversizedFileCount += 1;

        pushToGTMDataLayer(ANALYTICS_EVENT_FILE_UPLOAD_FAILED_SIZE_LIMIT, {
          file_size: file.size,
          file_type: file.type,
          max_file_size: maxFileSizeBytes,
        });

        continue;
      }

      acceptedFiles.push(file);
    }

    if (oversizedFileCount > 0) {
      const hasMultipleOversizedFiles = oversizedFileCount > 1;

      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        autoDismiss: true,
        title: hasMultipleOversizedFiles
          ? 'Error uploading the files'
          : 'Error uploading the file',
        message: hasMultipleOversizedFiles
          ? `The uploaded files exceed the maximum allowed size of ${formatFileSize(
              maxFileSizeBytes
            )}. Please try again with smaller files.`
          : `The uploaded file exceeds the maximum allowed size of ${formatFileSize(
              maxFileSizeBytes
            )}. Please try again with a smaller file.`,
      });

      pushToGTMDataLayer(ANALYTICS_EVENT_FILE_UPLOAD_ERROR_DISPLAYED, {
        reason: 'size_limit',
      });
    }

    for (const file of acceptedFiles) {
      let attachment: AttachmentItem;

      if (['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        let icon = '';
        const extension = file.type.split('/')[1];
        const compressedFile = await resizeImage(file, extension);
        icon = (await fileToBase64(compressedFile)).toString();

        attachment = {
          id: uuidv4(),
          file: file,
          status: 'pending',
          isImage: true,
          icon: icon ?? '',
        };
      } else {
        attachment = {
          file,
          id: uuidv4(),
          status: 'pending',
          icon: mapIcon(file, undefined),
        };
      }
      attachments.push(attachment);
      addAttachment(attachment);
    }

    if (attachments.length === 0) {
      return [];
    }

    return await uploadAll(attachments);
  };

  return {
    addAttachments,
    addAttachmentsViaEvent,
    resetAttachments,
    getAttachmentsCount,
    hasUploadErrors,
    hasUploadsInProgress,
    uploadedFilesMap,
    uploadedS3ObjectsMap,
    getUploadedS3ObjectsMap,
  };
};
